import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CmpsCardRowMenuItem,
    CmpsCardRowSubMenuItem,
    RowType,
} from '@cmps/ng-kit';
import { subDays } from 'date-fns';

import { AuthService } from '../auth/auth.service';
import { KpiType } from '../coaching-insight/insight-widget/model/kpi-type.model';
import { TakeUntilDestroy } from '../common/take-until-destroy.decorator';
import { TrendDialogMode } from '../scorecard-mi2/trend-dialog';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';
import { PeriodType } from '../widget/date-range.widget.model';

import { CoachingTrendData } from './models/coaching-trend-data.model';
import { OaScoreType } from './models/oa-score-type.enum';
import {
    clearState,
    closeAllCharts,
    reload,
    selectPeerGroup,
    setChartData,
    toggleSelectedRow,
} from './my-coaching-card.action';
import { TranslationKeyMap } from './my-coaching-card.i18n';
import { coachingCardReducer } from './my-coaching-card.reducer';
import { CoachingCardState } from './my-coaching-card.state';

@Component({
    selector: '[cmpsMyCoachingCard]',
    templateUrl: './my-coaching-card.component.html',
})
@TakeUntilDestroy()
@UseStore<CoachingCardState>('coachingCard', coachingCardReducer)
export class MyCoachingCardComponent
    implements OnInit, OnDestroy, StateToProps<CoachingCardState>
{
    state: CoachingCardState;

    /** @private */ _isExpanded: boolean;
    /** @private */ _chartData: CoachingTrendData;
    /** @private */ _selectedRows: { [key: number]: boolean };

    /** @private */ _oaScoreType = OaScoreType;
    /** @private */ _trendDialogMode = TrendDialogMode;
    /** @private */ _rowType = RowType;
    /** @private */ _i18n = TranslationKeyMap;

    constructor(
        private readonly _authService: AuthService,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
    ) {
        this._isExpanded = false;
        this._selectedRows = {};
    }

    ngOnInit(): void {
        this._onReload();
    }

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _toggleExpand(rowType?: RowType): void {
        this._isExpanded = !this._isExpanded;
        const cardElement =
            document.getElementsByClassName('coaching-card')[0].parentElement
                .classList;
        cardElement.toggle('insight-component-item--expanded');

        if (this._isExpanded) {
            let kpiType;

            switch (rowType) {
                case RowType.FLOA:
                    kpiType = KpiType.FRONT_LINE_OA;
                    break;
                case RowType.MI2:
                    kpiType = KpiType.MANAGEMENT_IMPROVEMENT_INDEX;
                    break;
                case RowType.FLMI2:
                    kpiType = KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX;
                    break;
                case RowType.WP:
                    kpiType = KpiType.WINNING_PERCENTAGE;
                    break;
                case RowType.FLWP:
                    kpiType = KpiType.FL_WINNING_PERCENTAGE;
                    break;
                default:
                    kpiType = KpiType.OPPORTUNITY_ALIGNMENT;
            }

            this._navigate(kpiType);
        }
    }

    /** @private */
    _onSelectItem(item: CmpsCardRowMenuItem | CmpsCardRowSubMenuItem): void {
        const id = item.id;
        switch (id) {
            case OaScoreType.Launch_OA:
                this._toggleExpand();
                break;
            default:
                this._dispatchSelectPeerGroup(id);
        }
        // index 0 is OA
        useDispatcher(
            this,
            setChartData({
                rowType: RowType.OA,
                rowIndex: 0,
            }),
        );
    }

    /** @private */
    _dispatchSelectPeerGroup(id?: OaScoreType, rowType?: RowType): void {
        useDispatcher(
            this,
            selectPeerGroup({
                selectedGroup: id,
                rowType,
            }),
        );
    }

    /** @private */
    _onReload(): void {
        const today = new Date();
        useDispatcher(
            this,
            reload({
                periodTypeId: PeriodType.ROLLING_30,
                effectiveDate: today,
                startDate: subDays(today, 29),
                endDate: today,
                userId: this._authService.user.id,
            }),
        );
        useDispatcher(this, closeAllCharts({}));
    }

    /** @private */
    _onClose(id: OaScoreType, rowType: RowType): void {
        useDispatcher(
            this,
            selectPeerGroup({
                selectedGroup: id,
            }),
        );

        // index 0 is OA
        useDispatcher(
            this,
            setChartData({
                rowType,
                rowIndex: 0,
            }),
        );
    }

    /** @private */
    _onRowClickToggleExpand(event: RowType): void {
        this._toggleExpand(event);
    }

    /** @private */
    _onTrendButtonClick(rowType: RowType, rowIndex: number): void {
        useDispatcher(
            this,
            setChartData({
                rowType,
                rowIndex,
            }),
        );
        useDispatcher(
            this,
            toggleSelectedRow({
                rowIndex,
            }),
        );
        this._dispatchSelectPeerGroup(null, rowType);
    }

    /** @private */
    _onCloseClick(rowIndex): void {
        useDispatcher(
            this,
            toggleSelectedRow({
                rowIndex,
            }),
        );
    }

    private _navigate(kpiTypeId: KpiType): void {
        void this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: {
                kpiTypeId,
                periodTypeId: PeriodType.ROLLING_30,
            },
            queryParamsHandling: 'merge',
        });
    }
}
